import { cn } from "cn";

import { Badge, type BadgeProps } from "@/components/ui/badge/badge";
import {
  IconType,
  ListItemStatusIcon,
} from "@/components/ui/status/list-item-status-icon";
import { ListItemStatus } from "@/types/openapi-enum";

import {
  isListItemStatus,
  type ListItemStatusValue,
  STATUS_LABELS,
} from "./list-item-field-labels";

/** The one status → colour map (ui-construction-methodology.md §2): the colours the status icon already uses. */
const STATUS_BADGE_COLORS: Record<
  ListItemStatusValue,
  NonNullable<BadgeProps["color"]>
> = {
  TODO: "fuchsia",
  IN_PROGRESS: "purple",
  DONE: "green",
  ARCHIVED: "black",
};

/** The icon takes the generated enum; naming each value here keeps the narrowing without a cast. */
const STATUS_ICON_VALUES: Record<ListItemStatusValue, ListItemStatus> = {
  TODO: ListItemStatus.Todo,
  IN_PROGRESS: ListItemStatus.InProgress,
  DONE: ListItemStatus.Done,
  ARCHIVED: ListItemStatus.Archived,
};

type ListItemStatusBadgeProps = {
  /** As the API sends it; renders nothing for a value this build does not know. */
  status: string | null | undefined;
  className?: string;
};

/**
 * A list item's status, shown inside the item: its icon and its word (To do, In progress,
 * Done, Archived), for the office and the member alike. `rounded`, not a pill: it reads as a
 * state, beside the type and "proposed by" tags, which stay pills.
 */
export function ListItemStatusBadge({
  status,
  className,
}: ListItemStatusBadgeProps) {
  if (!isListItemStatus(status)) return null;
  return (
    <Badge
      kind="tonal"
      color={STATUS_BADGE_COLORS[status]}
      shape="rounded"
      size="xs"
      className={cn("text-xs", className)}
    >
      <ListItemStatusIcon
        status={STATUS_ICON_VALUES[status]}
        size={16}
        type={IconType.Line}
      />
      {STATUS_LABELS[status]}
    </Badge>
  );
}
